// ✅ src/pages/VehicleV2/DayWiseLubeConsumptionReport.jsx
// UPDATED — Production listing template
// 3-column filters • page overflow locked • day columns scroll in table

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaEye,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/daywiselubeConsumptionReport";

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const monthNameToNumber = (m) => {
  const map = {
    january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
    july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  };
  return map[toStr(m).toLowerCase()] || 0;
};

const daysInMonth = (year, monthName) => {
  const y = Number(year);
  const m = monthNameToNumber(monthName);
  if (!Number.isFinite(y) || !m) return 0;
  return new Date(y, m, 0).getDate();
};

const fmtNum = (v, digits = 2) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const exportToCsv = (rows, columns, filename = "day-wise-lube-consumption.csv") => {
  if (!rows?.length) return;
  const headers = columns?.length ? columns : Object.keys(rows[0] || {});
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers.map((h) => `"${String(r?.[h] ?? "").replace(/"/g, '""')}"`).join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const createDefaultFilters = (branchType, branchId) => ({
  branchId: branchType === "HO" ? "Select" : String(branchId),
  month: "Select",
  year: "Select",
  regNo: "",
  vehicleName: "",
  vehicleCode: "",
  vehicleMainTypeId: "Select",
  itemCategoryId: "",
  itemId: "Select",
  itemNameText: "",
});

const IMPORTANT_COL_RE = /name|reg|code|vehicle|item|total|qty|qtytotal|branch/i;
const ID_COL_RE = /(^id$|_id$|typeid|categoryid|branchid)/i;
const DAY_COL_RE = /^(d)?([1-9]|[12][0-9]|3[01])$/i;

export default function DayWiseLubeConsumptionReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "BRANCH";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toNum(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "BRANCH"),
      loginUserId: toNum(loginUserId, 0),
      userId: toStr(userId || "0"),
    }),
    [sessionBranchId, branchType, loginUserId, userId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  const didInitRef = useRef(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const isBusy = pageLoading || dropdownLoading || listLoading;
  const [showFilters, setShowFilters] = useState(true);
  const [vehicleType, setVehicleType] = useState("O");
  const [branches, setBranches] = useState([]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([]);
  const [itemCategory, setItemCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [highlightItems, setHighlightItems] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const months = useMemo(
    () => ["January","February","March","April","May","June","July","August","September","October","November","December"],
    []
  );
  const years = useMemo(() => {
    const y = new Date().getFullYear();
    const arr = [];
    for (let i = 2006; i <= y; i++) arr.push(String(i));
    return arr;
  }, []);
  const totalDays = useMemo(() => daysInMonth(filters.year, filters.month), [filters.year, filters.month]);

  const fieldClass = useMemo(
    () =>
      `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );
  const labelClass = useMemo(
    () => `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const allColumns = useMemo(() => {
    if (columns?.length) return columns;
    if (rows?.[0]) return Object.keys(rows[0]);
    return [];
  }, [columns, rows]);

  const displayColumns = useMemo(() => {
    if (!allColumns.length) return [];
    const kept = allColumns.filter((c) => IMPORTANT_COL_RE.test(c) || DAY_COL_RE.test(String(c)) || /total/i.test(c));
    const cleaned = kept.filter((c) => !ID_COL_RE.test(String(c)));
    return cleaned.length ? cleaned : allColumns.slice(0, 8);
  }, [allColumns]);

  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, currentPage]);

  const loadHighlightItems = useCallback(async (nextFilters = filters, nextVehicleType = vehicleType) => {
    try {
      const month = toStr(nextFilters.month);
      const year = toStr(nextFilters.year);
      const branchIdQ = session.branchType === "HO" ? toStr(nextFilters.branchId) : String(session.branchId);
      if (isSelect(month) || isSelect(year)) {
        setHighlightItems([]);
        return;
      }
      const { data } = await axios.get(`${API_BASE}/highlight-items`, {
        params: { month, year, branchId: branchIdQ, vehType: nextVehicleType },
        headers: commonHeaders,
      });
      setHighlightItems(data?.success && Array.isArray(data.items) ? data.items : []);
    } catch {
      setHighlightItems([]);
    }
  }, [filters, vehicleType, session.branchType, session.branchId, commonHeaders]);

  const loadMeta = useCallback(async () => {
    try {
      setDropdownLoading(true);
      const { data } = await axios.get(`${API_BASE}/meta`, {
        params: {
          branchType: session.branchType,
          branchId: session.branchId,
          loginUserId: session.loginUserId,
        },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Meta failed");
      const br = toList(data.branches);
      const vmt = toList(data.vehicleMainTypes);
      const cat = data.itemCategory || null;
      const it = toList(data.items);
      setBranches(br);
      setVehicleMainTypes(vmt);
      setItemCategory(cat);
      setItems(it);
      setFilters((prev) => ({
        ...prev,
        branchId: session.branchType === "HO" ? prev.branchId : String(session.branchId),
        itemCategoryId: String(cat?.fld_itemcategoryid ?? prev.itemCategoryId ?? ""),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load meta");
      setBranches([]);
      setVehicleMainTypes([]);
      setItemCategory(null);
      setItems([]);
      setHighlightItems([]);
    } finally {
      setDropdownLoading(false);
    }
  }, [session.branchType, session.branchId, session.loginUserId, commonHeaders]);

  const buildListParams = useCallback((f = filters, nextVehicleType = vehicleType) => ({
    vehType: nextVehicleType,
    branchType: session.branchType,
    sessionBranchId: session.branchId,
    loginUserId: session.loginUserId,
    branchId: session.branchType === "HO" ? (isSelect(f.branchId) ? "Select" : toStr(f.branchId)) : String(session.branchId),
    month: isSelect(f.month) ? "Select" : toStr(f.month),
    year: isSelect(f.year) ? "Select" : toStr(f.year),
    regNo: toStr(f.regNo),
    vehicleCode: toStr(f.vehicleCode),
    vehicleName: toStr(f.vehicleName),
    vehicleMainTypeId: isSelect(f.vehicleMainTypeId) ? "Select" : toStr(f.vehicleMainTypeId),
    itemCategoryId: toStr(f.itemCategoryId),
    itemId: isSelect(f.itemId) ? "Select" : toStr(f.itemId),
    itemName: toStr(f.itemNameText),
  }), [filters, vehicleType, session.branchType, session.branchId, session.loginUserId]);

  const loadList = useCallback(async (nextFilters = filters, nextVehicleType = vehicleType, silent = false) => {
    try {
      setListLoading(true);
      const { data } = await axios.get(`${API_BASE}/list`, {
        params: buildListParams(nextFilters, nextVehicleType),
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Search failed");
      const list = toList(data.rows);
      const cols = toList(data.columns)?.length ? data.columns : list[0] ? Object.keys(list[0]) : [];
      setRows(list);
      setColumns(cols);
      setCurrentPage(1);
      if (!silent) toast.success(`Found ${list.length} records`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Search failed");
      setRows([]);
      setColumns([]);
      setCurrentPage(1);
    } finally {
      setListLoading(false);
    }
  }, [filters, vehicleType, buildListParams, commonHeaders]);

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      setFilters(createDefaultFilters(session.branchType, session.branchId));
      setRows([]);
      setColumns([]);
      setHighlightItems([]);
      setCurrentPage(1);
      await loadMeta();
    } finally {
      setPageLoading(false);
    }
  }, [session.branchType, session.branchId, loadMeta]);

  const handleChange = useCallback(async (e) => {
    const { name, value } = e.target;
    let nextFilters = null;
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "itemId") {
        const selected = items.find((x) => String(x.fld_itemid) === String(value));
        next.itemNameText = selected?.fld_itemname || "";
      }
      nextFilters = next;
      return next;
    });
    if (name === "month" || name === "year" || name === "branchId") {
      await loadHighlightItems(nextFilters || { ...filters, [name]: value }, vehicleType);
    }
  }, [items, filters, vehicleType, loadHighlightItems]);

  const validate = useCallback((f = filters) => {
    if (isSelect(f.month)) return "Please Select the Month";
    if (isSelect(f.year)) return "Please Select the Year";
    if (isSelect(f.itemId)) return "Please Select the Item Name";
    return "";
  }, [filters]);

  const handleSearch = useCallback(async () => {
    const msg = validate(filters);
    if (msg) return toast.warning(msg);
    await loadList(filters, vehicleType);
  }, [filters, vehicleType, validate, loadList]);

  const handleViewAll = useCallback(async () => {
    const reset = { ...filters, regNo: "", vehicleName: "", vehicleCode: "", vehicleMainTypeId: "Select" };
    const msg = validate(reset);
    if (msg) return toast.warning(msg);
    setFilters(reset);
    await loadList(reset, vehicleType);
    toast.info("Showing all vehicles for selected month/year/item");
  }, [filters, vehicleType, validate, loadList]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      ...createDefaultFilters(session.branchType, session.branchId),
      itemCategoryId: toStr(itemCategory?.fld_itemcategoryid || ""),
    });
    setRows([]);
    setColumns([]);
    setHighlightItems([]);
    setCurrentPage(1);
    toast.info("Cleared filters");
  }, [session.branchType, session.branchId, itemCategory]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleExport = useCallback(() => {
    if (!rows.length) return toast.warning("No data to export");
    exportToCsv(rows, allColumns, "day-wise-lube-consumption.csv");
    toast.success("Exported CSV");
  }, [rows, allColumns]);

  const handlePrint = useCallback(() => {
    const msg = validate(filters);
    if (msg) return toast.warning(msg);
    const qs = new URLSearchParams({
      vehType: vehicleType,
      branchId: session.branchType === "HO" ? (isSelect(filters.branchId) ? "Select" : toStr(filters.branchId)) : String(session.branchId),
      month: toStr(filters.month),
      year: toStr(filters.year),
      regNo: toStr(filters.regNo),
      vehicleCode: toStr(filters.vehicleCode),
      vehicleName: toStr(filters.vehicleName),
      vehicleMainTypeId: isSelect(filters.vehicleMainTypeId) ? "Select" : toStr(filters.vehicleMainTypeId),
      itemCategoryId: toStr(filters.itemCategoryId),
      itemId: isSelect(filters.itemId) ? "Select" : toStr(filters.itemId),
      itemName: toStr(filters.itemNameText),
    }).toString();
    window.open(`/vehicle/DayWIseLubeConsumptionReport_Print?${qs}`, "_blank");
  }, [filters, vehicleType, validate, session.branchType, session.branchId]);

  const handleVehicleTypeChange = useCallback(async (nextType) => {
    setVehicleType(nextType);
    await loadHighlightItems(filters, nextType);
  }, [filters, loadHighlightItems]);

  const handleLubeOutward = useCallback(() => {
    navigate("/vehicle/DayWiseLubeOutwardReport", {
      state: {
        vehType: vehicleType,
        branchId: session.branchType === "HO" ? filters.branchId : String(session.branchId),
        month: filters.month,
        year: filters.year,
        regNo: filters.regNo,
        vehicleCode: filters.vehicleCode,
        vehicleName: filters.vehicleName,
        vehicleMainTypeId: filters.vehicleMainTypeId,
        itemCategoryId: filters.itemCategoryId,
        itemId: filters.itemId,
        itemName: filters.itemNameText,
      },
    });
  }, [navigate, vehicleType, filters, session.branchType, session.branchId]);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  const paginate = useCallback((page) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
  }, [totalPages]);

  const statusText = pageLoading
    ? "Loading page..."
    : dropdownLoading
    ? "Loading dropdowns..."
    : listLoading
    ? "Searching..."
    : "Ready";

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">DAY WISE LUBE CONSUMPTION REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>
                Day Wise Lube Consumption Report
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} • User ID: {session.loginUserId || "-"}
              </p>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handlePrint} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaPrint /> Print</button>
              <button onClick={handleLubeOutward} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 bg-purple-600 hover:bg-purple-700 text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>Lube Outward</button>
              <button onClick={handleExport} disabled={isBusy || rows.length === 0} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}><FaFileExport /> Export</button>
              <button onClick={handleRefresh} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSyncAlt /> Refresh</button>
              <button onClick={() => navigate(-1)} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaArrowLeft /> Back</button>
              <button onClick={() => setShowFilters((s) => !s)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" disabled={isBusy} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>Clear</button>
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} disabled={isBusy} type="button" className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${isBusy ? "opacity-60 cursor-not-allowed" : ""} ${buttonClass} ${hoverClass} text-white`}><FaSearch /> {listLoading ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="flex gap-6 mb-4 flex-wrap">
                <label className={`flex items-center gap-2 text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <input type="radio" checked={vehicleType === "O"} onChange={() => handleVehicleTypeChange("O")} disabled={isBusy} />
                  Own Vehicle
                </label>
                <label className={`flex items-center gap-2 text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <input type="radio" checked={vehicleType === "H"} onChange={() => handleVehicleTypeChange("H")} disabled={isBusy} />
                  Hire Vehicle
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select name="branchId" value={filters.branchId} onChange={handleChange} className={fieldClass} disabled={isBusy || session.branchType !== "HO"}>
                    <option value="Select">Select</option>
                    {branches.map((b, idx) => (
                      <option key={idx} value={String(b.fld_branchid ?? b.branchid ?? b.id ?? b.value ?? "")}>
                        {b.fld_BranchName ?? b.branchname ?? b.name ?? b.label ?? ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {months.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <input type="text" name="regNo" value={filters.regNo} onChange={handleChange} className={fieldClass} placeholder="Enter registration number" disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Name</label>
                  <input type="text" name="vehicleName" value={filters.vehicleName} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle name" disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Code</label>
                  <input type="text" name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle code" disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select name="vehicleMainTypeId" value={filters.vehicleMainTypeId} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {vehicleMainTypes.map((v, idx) => (
                      <option key={idx} value={String(v.fld_vehicletypeid ?? v.id ?? v.value ?? v.fld_vehiclemaintypeid ?? "Select")}>
                        {v.fld_vehicletypename ?? v.name ?? v.label ?? v.fld_vehicletypeindex ?? ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Category</label>
                  <input className={fieldClass} value={itemCategory?.fld_itemcategory_name || "FUELS AND LUBRICANTS"} readOnly />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Name</label>
                  <select name="itemId" value={filters.itemId} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {items.map((it) => {
                      const name = it.fld_itemname;
                      const isHi = highlightItems.includes(name);
                      return (
                        <option key={it.fld_itemid} value={String(it.fld_itemid)} style={isHi ? { backgroundColor: "#00e6e6" } : undefined}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex gap-2 flex-wrap">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>Total Records: {rows.length}</div>
              {!!totalDays && !isSelect(filters.month) && !isSelect(filters.year) && (
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-gray-800 text-gray-200" : "bg-gray-100 text-gray-800"}`}>Days: {totalDays}</div>
              )}
            </div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{statusText}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || dropdownLoading || listLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Select Month, Year and Item Name, then click Search.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    {displayColumns.map((c) => (
                      <th key={c} className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {pagedRows.map((r, idx) => (
                    <tr key={idx} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      {displayColumns.map((c) => {
                        const v = r?.[c];
                        const isNum = typeof v === "number" || (typeof v === "string" && v !== "" && !Number.isNaN(Number(v)));
                        return (
                          <td key={c} className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {isNum ? fmtNum(v, 2) : toStr(v)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}